/**
 * Guest Service
 *
 * Fetches data from the backend API.
 * Guest is a thin role record — personal data lives in Person.
 * See docs/api/BACKEND.md § Guest model for source of truth.
 */

import {
  Guest,
  GuestCreateFromPersonInput,
  GuestCreateWithPersonInput,
  GuestFormData,
  GuestListFilters,
  PaginatedGuestResponse,
  GuestCharge,
  GuestChargeSettleInput,
  GuestChargeSettleResult,
  GuestChargeSellableInput,
  GuestStatus,
  GuestOffering,
  GuestOfferingInput,
  GuestOfferingIssue,
  GuestOfferingIssueInput,
  GuestDeposit,
} from '../types';
import type { ExpenseStatement } from '@/modules/booking/types';
import { getBookingGuestStays, updateBookingGuestStay } from '@/modules/booking/services/bookingService';
import { mapBackendPerson, splitName, joinName } from '@/entities/person';
import { syncPersonPrimaryPhone } from '@/entities/person/services/personService';
import { apiClient } from '@/shared/services/api';

// ---------------------------------------------------------------------------
// Backend response shapes
// ---------------------------------------------------------------------------

/**
 * NOTE: apiClient transforms snake_case → camelCase in responses.
 * All interfaces use camelCase keys.
 */

/** Detail response: person is a nested object */
interface BackendGuestDetail {
  id: string;
  person: Record<string, unknown>;
  location: string;
  guestSourceType?: string;
  guest_source_type?: string;
  booking?: string | null;
  bookingStatus?: 'pending' | 'confirmed' | 'checked_in' | 'checked_out' | 'cancelled' | 'no_show' | null;
  booking_status?: 'pending' | 'confirmed' | 'checked_in' | 'checked_out' | 'cancelled' | 'no_show' | null;
  hostStaff?: string | null;
  host_staff?: string | null;
  isActive: boolean;
  paymentMode?: 'room_charge' | 'prepaid' | null;
  blLastPushedAt: string | null;
  status?: GuestStatus;
  createdAt: string;
  updatedAt: string;
  source?: string;
  externalId?: string;
  editPolicy?: 'native' | 'bl-read-only';
}

/** List response: person data is flattened */
interface BackendGuestList {
  id: string;
  /** The till's own answer; see Guest.canChargeRoom. */
  canChargeRoom?: boolean;
  personId?: string;
  person_id?: string;
  name?: string;
  email?: string;
  phone?: string;
  nationality?: string;
  allergies?: string[];
  location?: string;
  guestSourceType?: string;
  guest_source_type?: string;
  booking?: string | null;
  bookingStatus?: 'pending' | 'confirmed' | 'checked_in' | 'checked_out' | 'cancelled' | 'no_show' | null;
  booking_status?: 'pending' | 'confirmed' | 'checked_in' | 'checked_out' | 'cancelled' | 'no_show' | null;
  bookingReference?: string | null;
  booking_reference?: string | null;
  bookingCheckIn?: string | null;
  booking_check_in?: string | null;
  bookingCheckOut?: string | null;
  booking_check_out?: string | null;
  hostStaff?: string | null;
  host_staff?: string | null;
  status?: Guest['status'];
  createdAt: string;
  created_at?: string;
  source?: string;
  externalId?: string;
  external_id?: string;
  editPolicy?: 'native' | 'bl-read-only';
}

interface BackendGuestCharge {
  id: string;
  booking: string;
  bookingNumber?: string | null;
  credential: string | null;
  sourceType?: string | null;
  sourceObjectId?: string | null;
  amount: string;
  description: string;
  status: 'open' | 'settled' | 'voided';
  settlementPayment: string | null;
  pendingObligation?: string | null;
  createdAt: string;
}

interface BackendGuestOffering {
  id: string;
  location: string;
  name: string;
  sourceKind: GuestOffering['sourceKind'];
  sourceModel: string;
  sourceObjectId: string;
  sourceName: string | null;
  sourceHasVariants: boolean;
  offeringKind: GuestOffering['offeringKind'];
  defaultChargeMode: GuestOffering['defaultChargeMode'];
  defaultDepositMode: GuestOffering['defaultDepositMode'];
  defaultDepositAmount: string | null;
  allowOverrideAtIssue: boolean;
  requiresReturn: boolean;
  notes: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface BackendGuestDeposit {
  id: string;
  issue: string;
  issueName: string;
  booking: string;
  bookingReference: string | null;
  guest: string;
  guestName: string;
  amount: string;
  currency: string;
  mode: GuestDeposit['mode'];
  status: GuestDeposit['status'];
  linkedGuestCharge: string | null;
  openedPayment: string | null;
  refundedPayment: string | null;
  notes: string;
  openedAt: string;
  releasedAt: string | null;
  forfeitedAt: string | null;
  refundedAt: string | null;
}

interface BackendGuestOfferingIssue {
  id: string;
  booking: string;
  bookingReference: string | null;
  guest: string;
  guestName: string;
  offering: string;
  offeringName: string;
  shopVariant: string | null;
  quantity: string;
  unitPrice: string;
  chargeAmount: string;
  chargeModeUsed: GuestOfferingIssue['chargeModeUsed'];
  depositModeUsed: GuestOfferingIssue['depositModeUsed'];
  status: GuestOfferingIssue['status'];
  sourceSnapshotName: string;
  notes: string;
  chargePayment: string | null;
  chargeGuestCharge: string | null;
  deposit: BackendGuestDeposit | null;
  issuedAt: string;
  returnedAt: string | null;
}

function mapBookingStatusToGuestStatus(bookingStatus?: string | null, isActive: boolean = true): Guest['status'] {
  if (!isActive) return 'checked-out';
  if (bookingStatus === 'checked_in') return 'checked-in';
  if (bookingStatus === 'checked_out') return 'checked-out';
  return 'pending';
}

function mapDetailGuest(b: BackendGuestDetail): Guest {
  const person = mapBackendPerson(b.person);
  const { firstName, lastName } = splitName(person.name);
  const bookingStatus = b.bookingStatus ?? null;
  const status: GuestStatus = b.status ?? mapBookingStatusToGuestStatus(bookingStatus, true);
  return {
    id: b.id,
    person,
    locationId: b.location ?? '',
    guestSourceType: (b.guestSourceType ?? 'booking') as Guest['guestSourceType'],
    bookingId: b.booking ?? null,
    hostStaffId: b.hostStaff ?? null,
    isActive: b.isActive ?? true,
    paymentMode: b.paymentMode ?? null,
    blLastPushedAt: b.blLastPushedAt ?? null,
    createdAt: b.createdAt,
    updatedAt: b.updatedAt,
    firstName,
    lastName,
    email: person.email,
    phone: person.primaryPhone?.phone || undefined,
    nationality: person.nationality || undefined,
    allergies: person.allergies,
    status,
    locationName: '',
    source: b.source,
    externalId: b.externalId,
    editPolicy: b.editPolicy ?? 'native',
  };
}

function mapListGuest(b: BackendGuestList): Guest {
  const name = b.name ?? '';
  const { firstName, lastName } = splitName(name);
  const person = mapBackendPerson({
    id: b.personId ?? b.person_id ?? '',
    name,
    email: b.email ?? '',
    phone: b.phone ?? '',
    nationality: b.nationality ?? '',
    allergies: b.allergies ?? [],
    current_role: 'guest',
    is_active: true,
    created_at: b.createdAt ?? b.created_at,
  });
  const bookingStatus = b.bookingStatus ?? null;
  const status: GuestStatus = b.status ?? mapBookingStatusToGuestStatus(bookingStatus, true);
  return {
    id: b.id,
    person,
    locationId: b.location ?? '',
    guestSourceType: (b.guestSourceType ?? 'booking') as Guest['guestSourceType'],
    bookingId: b.booking ?? null,
    hostStaffId: b.hostStaff ?? null,
    isActive: status !== 'checked-out',
    blLastPushedAt: null,
    createdAt: b.createdAt ?? b.created_at ?? '',
    updatedAt: b.createdAt ?? b.created_at ?? '',
    firstName,
    lastName,
    email: b.email ?? '',
    phone: b.phone || undefined,
    nationality: b.nationality || undefined,
    allergies: b.allergies ?? [],
    status,
    // Left undefined when the server does not send it, NOT defaulted to
    // false: "we don't know" and "the till would refuse" are different, and
    // an older backend must degrade to showing no badge rather than warning
    // about every guest in the house.
    canChargeRoom: b.canChargeRoom,
    locationName: '',
    bookingReference: b.bookingReference ?? null,
    checkInDate: b.bookingCheckIn ?? null,
    checkOutDate: b.bookingCheckOut ?? null,
    source: b.source,
    externalId: b.externalId ?? b.external_id,
    editPolicy: b.editPolicy ?? 'native',
  };
}

function mapGuestCharge(b: BackendGuestCharge): GuestCharge {
  return {
    id: b.id,
    bookingId: b.booking,
    bookingNumber: b.bookingNumber ?? null,
    credentialId: b.credential ?? null,
    sourceType: b.sourceType ?? null,
    sourceObjectId: b.sourceObjectId ?? null,
    amount: b.amount,
    description: b.description,
    status: b.status,
    settlementPaymentId: b.settlementPayment ?? null,
    pendingObligationId: b.pendingObligation ?? null,
    createdAt: b.createdAt,
  };
}

function mapGuestDeposit(b: BackendGuestDeposit): GuestDeposit {
  return {
    id: b.id,
    issueId: b.issue,
    issueName: b.issueName,
    bookingId: b.booking,
    bookingReference: b.bookingReference ?? null,
    guestId: b.guest,
    guestName: b.guestName,
    amount: b.amount,
    currency: b.currency,
    mode: b.mode,
    status: b.status,
    linkedGuestChargeId: b.linkedGuestCharge ?? null,
    openedPaymentId: b.openedPayment ?? null,
    refundedPaymentId: b.refundedPayment ?? null,
    notes: b.notes,
    openedAt: b.openedAt,
    releasedAt: b.releasedAt ?? null,
    forfeitedAt: b.forfeitedAt ?? null,
    refundedAt: b.refundedAt ?? null,
  };
}

function mapGuestOffering(b: BackendGuestOffering): GuestOffering {
  return {
    id: b.id,
    locationId: b.location,
    name: b.name,
    sourceKind: b.sourceKind,
    sourceModel: b.sourceModel,
    sourceObjectId: b.sourceObjectId,
    sourceName: b.sourceName ?? null,
    sourceHasVariants: Boolean(b.sourceHasVariants),
    offeringKind: b.offeringKind,
    defaultChargeMode: b.defaultChargeMode,
    defaultDepositMode: b.defaultDepositMode,
    defaultDepositAmount: b.defaultDepositAmount ?? null,
    allowOverrideAtIssue: Boolean(b.allowOverrideAtIssue),
    requiresReturn: Boolean(b.requiresReturn),
    notes: b.notes ?? '',
    isActive: Boolean(b.isActive),
    createdAt: b.createdAt,
    updatedAt: b.updatedAt,
  };
}

function mapGuestOfferingIssue(b: BackendGuestOfferingIssue): GuestOfferingIssue {
  return {
    id: b.id,
    bookingId: b.booking,
    bookingReference: b.bookingReference ?? null,
    guestId: b.guest,
    guestName: b.guestName,
    offeringId: b.offering,
    offeringName: b.offeringName,
    shopVariantId: b.shopVariant ?? null,
    quantity: b.quantity,
    unitPrice: b.unitPrice,
    chargeAmount: b.chargeAmount,
    chargeModeUsed: b.chargeModeUsed,
    depositModeUsed: b.depositModeUsed,
    status: b.status,
    sourceSnapshotName: b.sourceSnapshotName,
    notes: b.notes ?? '',
    chargePaymentId: b.chargePayment ?? null,
    chargeGuestChargeId: b.chargeGuestCharge ?? null,
    deposit: b.deposit ? mapGuestDeposit(b.deposit) : null,
    issuedAt: b.issuedAt,
    returnedAt: b.returnedAt ?? null,
  };
}

// ---------------------------------------------------------------------------
// Service functions
// ---------------------------------------------------------------------------

export const getAllGuests = async (
  filters?: { bookingStatus?: 'pending' | 'confirmed' | 'checked_in' | 'checked_out' | 'cancelled' | 'no_show' },
): Promise<Guest[]> => {
  const params: Record<string, string | number> = { page_size: 200 };
  if (filters?.bookingStatus) params.booking_status = filters.bookingStatus;
  const response = await apiClient.get<{ results: BackendGuestList[]; count: number }>(
    '/api/v1/guests/',
    { params },
  );
  return response.data.results.map(mapListGuest);
};

export const getGuestById = async (
  id: string,
  options?: { skipLocationFilter?: boolean },
): Promise<Guest> => {
  const response = await apiClient.get<BackendGuestDetail>(`/api/v1/guests/${id}/`, {
    showErrorToast: false,
    skipLocationFilter: options?.skipLocationFilter,
  });
  return mapDetailGuest(response.data);
};

export const getGuestByPersonId = async (
  personId: string,
  options?: { skipLocationFilter?: boolean },
): Promise<Guest | null> => {
  const response = await apiClient.get<{ results: BackendGuestList[] }>(
    '/api/v1/guests/',
    {
      params: { person: personId, page_size: 1 },
      skipLocationFilter: options?.skipLocationFilter,
    },
  );
  const first = response.data.results[0];
  if (!first) return null;
  return getGuestById(first.id, options);
};

export const getGuestChargesByBookingId = async (
  bookingId: string,
  options?: { skipLocationFilter?: boolean },
): Promise<GuestCharge[]> => {
  const response = await apiClient.get<{ results: BackendGuestCharge[] }>(
    '/api/v1/guest-charges/',
    {
      params: { booking: bookingId, page_size: 200 },
      skipLocationFilter: options?.skipLocationFilter,
    },
  );
  return response.data.results.map(mapGuestCharge);
};

export const settleGuestCharge = async (
  chargeId: string,
  input: GuestChargeSettleInput,
): Promise<GuestChargeSettleResult> => {
  const response = await apiClient.post<{ charge: BackendGuestCharge; paymentId: string }>(
    `/api/v1/guest-charges/${chargeId}/settle/`,
    {
      payment_method: input.paymentMethod,
      ...(input.paidByPerson ? { paid_by_person: input.paidByPerson } : {}),
      ...(input.notes ? { notes: input.notes } : {}),
    },
  );
  return {
    charge: mapGuestCharge(response.data.charge),
    // apiClient camelCase-transforms responses: the field is paymentId, not payment_id.
    paymentId: response.data.paymentId,
  };
};

export const createGuestChargeSellable = async (
  input: GuestChargeSellableInput,
): Promise<GuestCharge> => {
  const response = await apiClient.post<BackendGuestCharge>(
    '/api/v1/guest-charges/charge-sellable/',
    {
      booking_id: input.bookingId,
      ...(input.productId ? { product_id: input.productId } : {}),
      ...(input.shopProductId ? { shop_product_id: input.shopProductId } : {}),
      ...(input.menuRecipeItemId ? { menu_recipe_item_id: input.menuRecipeItemId } : {}),
      ...(input.menuProductItemId ? { menu_product_item_id: input.menuProductItemId } : {}),
      ...(input.catalogItemId ? { catalog_item_id: input.catalogItemId } : {}),
      ...(input.serviceProductId ? { service_product_id: input.serviceProductId } : {}),
      quantity: input.quantity,
      ...(input.description ? { description: input.description } : {}),
      ...(input.credentialId ? { credential: input.credentialId } : {}),
      ...(input.approverPassword ? { approver_password: input.approverPassword } : {}),
      ...(input.approverNfcUid ? { approver_nfc_uid: input.approverNfcUid } : {}),
      ...(input.overrideReason ? { override_reason: input.overrideReason } : {}),
    },
  );
  return mapGuestCharge(response.data);
};

export const getGuestsByIds = async (ids: string[]): Promise<Guest[]> => {
  if (ids.length === 0) return [];
  const uniqueIds = Array.from(new Set(ids));
  return Promise.all(
    uniqueIds.map(async (id) => {
      const response = await apiClient.get<BackendGuestDetail>(`/api/v1/guests/${id}/`, { showErrorToast: false });
      return mapDetailGuest(response.data);
    }),
  );
};

export const getGuestOfferings = async (
  filters?: { offeringKind?: string; isActive?: boolean; search?: string },
): Promise<GuestOffering[]> => {
  const params: Record<string, string | boolean | number> = { page_size: 200 };
  if (filters?.offeringKind) params.offering_kind = filters.offeringKind;
  if (filters?.isActive !== undefined) params.is_active = filters.isActive;
  if (filters?.search) params.search = filters.search;
  const response = await apiClient.get<{ results: BackendGuestOffering[] }>(
    '/api/v1/guest-offerings/',
    { params },
  );
  return response.data.results.map(mapGuestOffering);
};

export const createGuestOffering = async (input: GuestOfferingInput): Promise<GuestOffering> => {
  const response = await apiClient.post<BackendGuestOffering>('/api/v1/guest-offerings/', {
    location: input.locationId,
    name: input.name,
    source_kind: input.sourceKind,
    source_id: input.sourceId,
    offering_kind: input.offeringKind,
    default_charge_mode: input.defaultChargeMode,
    default_deposit_mode: input.defaultDepositMode,
    default_deposit_amount: input.defaultDepositAmount ?? null,
    allow_override_at_issue: input.allowOverrideAtIssue,
    requires_return: input.requiresReturn,
    notes: input.notes ?? '',
    is_active: input.isActive ?? true,
  });
  return mapGuestOffering(response.data);
};

export const updateGuestOffering = async (
  id: string,
  input: Partial<GuestOfferingInput>,
): Promise<GuestOffering> => {
  const payload: Record<string, unknown> = {};
  if (input.locationId !== undefined) payload.location = input.locationId;
  if (input.name !== undefined) payload.name = input.name;
  if (input.sourceKind !== undefined) payload.source_kind = input.sourceKind;
  if (input.sourceId !== undefined) payload.source_id = input.sourceId;
  if (input.offeringKind !== undefined) payload.offering_kind = input.offeringKind;
  if (input.defaultChargeMode !== undefined) payload.default_charge_mode = input.defaultChargeMode;
  if (input.defaultDepositMode !== undefined) payload.default_deposit_mode = input.defaultDepositMode;
  if (input.defaultDepositAmount !== undefined) payload.default_deposit_amount = input.defaultDepositAmount;
  if (input.allowOverrideAtIssue !== undefined) payload.allow_override_at_issue = input.allowOverrideAtIssue;
  if (input.requiresReturn !== undefined) payload.requires_return = input.requiresReturn;
  if (input.notes !== undefined) payload.notes = input.notes;
  if (input.isActive !== undefined) payload.is_active = input.isActive;
  const response = await apiClient.patch<BackendGuestOffering>(`/api/v1/guest-offerings/${id}/`, payload);
  return mapGuestOffering(response.data);
};

export const deleteGuestOffering = async (id: string): Promise<void> => {
  await apiClient.delete(`/api/v1/guest-offerings/${id}/`);
};

export const getGuestOfferingIssues = async (
  filters?: { bookingId?: string; guestId?: string; status?: string },
): Promise<GuestOfferingIssue[]> => {
  const params: Record<string, string | number> = { page_size: 200 };
  if (filters?.bookingId) params.booking = filters.bookingId;
  if (filters?.guestId) params.guest = filters.guestId;
  if (filters?.status) params.status = filters.status;
  const response = await apiClient.get<{ results: BackendGuestOfferingIssue[] }>(
    '/api/v1/guest-offering-issues/',
    { params },
  );
  return response.data.results.map(mapGuestOfferingIssue);
};

export const issueGuestOffering = async (input: GuestOfferingIssueInput): Promise<GuestOfferingIssue> => {
  const response = await apiClient.post<BackendGuestOfferingIssue>('/api/v1/guest-offering-issues/', {
    booking: input.bookingId,
    guest: input.guestId,
    offering: input.offeringId,
    quantity: input.quantity,
    charge_mode: input.chargeMode,
    deposit_mode: input.depositMode,
    payment_method: input.paymentMethod,
    deposit_payment_method: input.depositPaymentMethod,
    shop_variant_id: input.shopVariantId ?? null,
    notes: input.notes ?? '',
  });
  return mapGuestOfferingIssue(response.data);
};

export const returnGuestOffering = async (
  id: string,
  input?: { notes?: string; refundPaymentMethod?: string },
): Promise<GuestOfferingIssue> => {
  const response = await apiClient.post<BackendGuestOfferingIssue>(`/api/v1/guest-offering-issues/${id}/return/`, {
    notes: input?.notes ?? '',
    refund_payment_method: input?.refundPaymentMethod,
  });
  return mapGuestOfferingIssue(response.data);
};

export const markGuestOfferingLost = async (id: string, notes?: string): Promise<GuestOfferingIssue> => {
  const response = await apiClient.post<BackendGuestOfferingIssue>(`/api/v1/guest-offering-issues/${id}/mark-lost/`, {
    notes: notes ?? '',
  });
  return mapGuestOfferingIssue(response.data);
};

export const markGuestOfferingDamaged = async (id: string, notes?: string): Promise<GuestOfferingIssue> => {
  const response = await apiClient.post<BackendGuestOfferingIssue>(`/api/v1/guest-offering-issues/${id}/mark-damaged/`, {
    notes: notes ?? '',
  });
  return mapGuestOfferingIssue(response.data);
};

export const getGuestDeposits = async (
  filters?: { bookingId?: string; guestId?: string; status?: string },
): Promise<GuestDeposit[]> => {
  const params: Record<string, string | number> = { page_size: 200 };
  if (filters?.bookingId) params.booking = filters.bookingId;
  if (filters?.guestId) params.guest = filters.guestId;
  if (filters?.status) params.status = filters.status;
  const response = await apiClient.get<{ results: BackendGuestDeposit[] }>(
    '/api/v1/guest-deposits/',
    { params },
  );
  return response.data.results.map(mapGuestDeposit);
};

export const searchGuests = async (query: string): Promise<Guest[]> => {
  const response = await apiClient.get<{ results: BackendGuestList[] }>(
    '/api/v1/guests/',
    { params: { search: query, page_size: 50 } },
  );
  return response.data.results.map(mapListGuest);
};

/**
 * Crear persona + ficha de huésped en UNA transacción (PENDINGS #71).
 *
 * Reemplaza el encadenado `createPerson` → `createGuestFromPerson`, cuyo fallo
 * a mitad dejaba una `Person` escrita mientras el mensaje decía que no se
 * había creado nada — y el reintento del recepcionista duplicaba la ficha.
 *
 * El backend acepta `person_input` con la misma forma que `POST /persons/`, y
 * construye la persona con su propio serializer, de modo que el documento
 * sigue pasando por el campo cifrado igual que por la otra puerta.
 */
export const createGuestWithPerson = async (
  input: GuestCreateWithPersonInput,
): Promise<Guest> => {
  const p = input.person;
  const response = await apiClient.post<BackendGuestDetail>('/api/v1/guests/', {
    location: input.locationId,
    guest_source_type: input.guestSourceType,
    booking: input.guestSourceType === 'booking' ? (input.bookingId ?? null) : null,
    host_staff: input.guestSourceType === 'staff_invite' ? (input.hostStaffId ?? null) : null,
    person_input: {
      name: [p.firstName, p.lastName].filter(Boolean).join(' '),
      first_name: p.firstName,
      last_name: p.lastName,
      email: (p.email ?? '').trim().toLowerCase(),
      nationality: p.nationality ?? '',
      birth_date: p.birthDate || null,
      document_number: p.documentNumber ?? '',
    },
  });
  return mapDetailGuest(response.data);
};

export const createGuestFromPerson = async (input: GuestCreateFromPersonInput): Promise<Guest> => {
  const response = await apiClient.post<BackendGuestDetail>('/api/v1/guests/', {
    person_id: input.personId,
    location: input.locationId,
    guest_source_type: input.guestSourceType,
    booking: input.guestSourceType === 'booking' ? (input.bookingId ?? null) : null,
    host_staff: input.guestSourceType === 'staff_invite' ? (input.hostStaffId ?? null) : null,
  });
  return mapDetailGuest(response.data);
};

/**
 * Update a guest.
 * Personal data (name, email, etc.) goes to the Person endpoint.
 * Guest flags/settings (is_active, payment_mode) go to the Guest endpoint.
 */
export const updateGuest = async (
  guestId: string,
  data: Partial<GuestFormData & { isActive?: boolean }>,
  personId?: string,
): Promise<Guest> => {
  const calls: Promise<unknown>[] = [];

  const personalFields: Record<string, unknown> = {};
  if (data.firstName !== undefined || data.lastName !== undefined) {
    personalFields.name = joinName(data.firstName ?? '', data.lastName ?? '');
  }
  if (data.email !== undefined) personalFields.email = data.email;
  if (data.nationality !== undefined) personalFields.nationality = data.nationality;
  if (data.nationalityIso !== undefined) personalFields.nationality_iso = data.nationalityIso;
  if (data.documentType !== undefined) personalFields.document_type = data.documentType;
  if (data.documentNumber !== undefined) personalFields.document_number = data.documentNumber;
  if (data.documentExpiration !== undefined) personalFields.document_expiration = data.documentExpiration;
  if (data.birthDate !== undefined) personalFields.birth_date = data.birthDate || null;
  if (data.allergies !== undefined) personalFields.allergies = data.allergies;
  if (data.notes !== undefined) personalFields.notes = data.notes;
  if (data.secondLastName !== undefined) personalFields.second_last_name = data.secondLastName;
  if (data.specialRequests !== undefined) personalFields.special_requests = data.specialRequests;
  if (data.marketingOptIn !== undefined) personalFields.marketing_opt_in = data.marketingOptIn;
  if (data.dataConsentGiven !== undefined) personalFields.data_consent_given = data.dataConsentGiven;

  if (personId && Object.keys(personalFields).length > 0) {
    calls.push(apiClient.patch(`/api/v1/persons/${personId}/`, personalFields));
  }
  if (personId && data.phone !== undefined) {
    calls.push(syncPersonPrimaryPhone(personId, data.phone));
  }

  const guestFields: Record<string, unknown> = {};
  if (data.isActive !== undefined) guestFields.is_active = data.isActive;
  if (data.paymentMode !== undefined) guestFields.payment_mode = data.paymentMode;

  if (Object.keys(guestFields).length > 0) {
    calls.push(apiClient.patch(`/api/v1/guests/${guestId}/`, guestFields));
  }

  await Promise.all(calls);

  const response = await apiClient.get<BackendGuestDetail>(`/api/v1/guests/${guestId}/`);
  return mapDetailGuest(response.data);
};

export const checkInGuest = async (guestId: string, data: Partial<GuestFormData>): Promise<Guest> => {
  const guest = await getGuestById(guestId);
  return updateGuest(guestId, data, guest.person?.id);
};

/**
 * Close a guest's stay.
 *
 * The stay check-out deactivates the guest inside the same server-side
 * transaction, so there is no second PATCH here: a separate
 * `is_active: false` call could not be atomic with it and would leave the
 * guest chargeable at the POS whenever it failed.
 *
 * The direct deactivation survives only for the case the stay path cannot
 * cover — a guest with no stay row at all, where nothing server-side would
 * otherwise run.
 */
export const checkOutGuest = async (guestId: string, stayId?: string | null): Promise<Guest> => {
  const guest = await getGuestById(guestId);

  let resolvedStayId = stayId ?? null;
  if (!resolvedStayId && guest.bookingId) {
    const stays = await getBookingGuestStays({
      bookingId: guest.bookingId,
      guestId,
      pageSize: 20,
    });
    resolvedStayId = stays.find(
      (stay) => !['checked_out', 'cancelled', 'no_show'].includes(stay.status),
    )?.id ?? null;
  }

  if (!resolvedStayId) {
    return updateGuest(guestId, { isActive: false }, guest.person?.id);
  }

  await updateBookingGuestStay(resolvedStayId, {
    status: 'checked_out',
    checkedOutAt: new Date(),
  });
  return getGuestById(guestId);
};

/**
 * Resolve the stay to act on for a guest status change. Prefers the explicit
 * stayId (from the booking detail's stayIdByGuestId map); otherwise finds the
 * guest's stay whose status matches one of `wantStatus`.
 */
const resolveGuestStayId = async (
  guestId: string,
  stayId: string | null | undefined,
  bookingId: string | undefined,
  wantStatus: string[],
): Promise<string | null> => {
  if (stayId) return stayId;
  if (!bookingId) return null;
  const stays = await getBookingGuestStays({ bookingId, guestId, pageSize: 20 });
  return stays.find((stay) => wantStatus.includes(stay.status))?.id ?? null;
};

/**
 * Undo an accidental check-in: the guest's stay goes back to `expected` and its
 * check-in timestamp is cleared. The booking status is recomputed server-side
 * from the remaining stays (back to `confirmed` once no stay is checked in).
 */
export const revertGuestCheckin = async (guestId: string, stayId?: string | null): Promise<void> => {
  const guest = await getGuestById(guestId);
  const id = await resolveGuestStayId(guestId, stayId, guest.bookingId, ['checked_in']);
  if (id) {
    await updateBookingGuestStay(id, { status: 'expected', checkedInAt: null });
  }
};

/**
 * Undo a check-out (e.g. done in error): the guest's stay returns to
 * `checked_in`, its check-out timestamp is cleared, and the guest is
 * re-activated (check-out had deactivated it).
 */
export const revertGuestCheckout = async (guestId: string, stayId?: string | null): Promise<Guest> => {
  const guest = await getGuestById(guestId);
  const id = await resolveGuestStayId(guestId, stayId, guest.bookingId, ['checked_out']);
  if (id) {
    await updateBookingGuestStay(id, { status: 'checked_in', checkedOutAt: null });
  }
  return updateGuest(guestId, { isActive: true }, guest.person?.id);
};

export const getGuestsPaginated = async (
  filters?: GuestListFilters,
  cursor?: string,
  limit: number = 20,
): Promise<PaginatedGuestResponse> => {
  let endpoint: string;
  let params: Record<string, string | number | boolean> | undefined;

  if (cursor) {
    endpoint = cursor;
  } else {
    endpoint = '/api/v1/guests/';
    params = { page_size: limit };
    if (filters?.search) params.search = filters.search;
    if (filters?.status) params.status = filters.status;
    if (filters?.nationality) params['person__nationality'] = filters.nationality;
  }

  const response = await apiClient.get<{
    results: BackendGuestList[];
    count: number;
    next: string | null;
  }>(endpoint, params ? { params } : {});

  return {
    data: response.data.results.map(mapListGuest),
    nextCursor: response.data.next,
    hasMore: !!response.data.next,
    totalCount: response.data.count,
  };
};

export const getGuestExpenseStatement = async (guestId: string): Promise<ExpenseStatement> => {
  const response = await apiClient.get<ExpenseStatement>(
    `/api/v1/guests/${guestId}/statement/`,
    { skipLocationFilter: true },
  );
  return response.data;
};
